import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth } from "../../http";

export type PagedResult<T> = { items: T[]; total: number; page: number; pageSize: number };


export type MarketplaceCompany = {
    id: number;
    name: string;
    shortName: string;
    description: string;
    city: string;
    country: string;
    countryCode: string;
    logoUrl: string | null;
    devicesCount: number | null;
    webSiteUrl: string;
    markets: number[];
    quickTags: number[];
};

export type MarketplaceCompanyDetails = {
    id: number;
    name: string;
    companyId: number;
    shortName?: string | null;
    city?: string | null;
    country?: string | null;
    countryCode?: string | null;
    street?: string | null;
    postalCode?: string | null;
    buildingNumber?: string | null;
    unitNumber?: string | null;
    phone?: string | null;
    email?: string | null;
    webSiteUrl?: string | null;
    yearFounded?: number | null;
    companySizeTierId?: number | null;
    logoUrl?: string | null;
    description?: string | null;
    markets: number[];
    certificates: number[];
    devices: MarketplaceDevice[];
    quickTags: number[];
};

export type MarketplaceDevice = {
    id: number;
    name: string;
    description: string;
    url: string;
    structureId: string;
}

export type LoadCompaniesParams = {
    q?: string;
    sort?: "name";
    sortDir?: "asc" | "desc";
    page?: number;
    pageSize?: number;
    structureId?: string;
};

export const loadMarketplaceCompanies = createAsyncThunk<
    PagedResult<MarketplaceCompany>,
    LoadCompaniesParams | void,
    AppThunkAPIType
>(
    "marketplace/loadCompanies",
    async (args, { dispatch, rejectWithValue }) => {
        const p = args ?? {};
        const params = new URLSearchParams();
        if (p.q) params.set("q", p.q);
        if (p.sort) params.set("sort", p.sort);
        if (p.sortDir) params.set("sortDir", p.sortDir);
        if (p.page) params.set("page", String(p.page));
        if (p.pageSize) params.set("pageSize", String(p.pageSize));
        if (p.structureId) params.set('structureId', String(p.structureId)); // <<< tu

        const url = `api/marketplace/companies${params.toString() ? `?${params.toString()}` : ""}`;

        const result = await dispatch(getWithAuth({ url }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as PagedResult<MarketplaceCompany>;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadCompanyDetails = createAsyncThunk<
    MarketplaceCompanyDetails,
    { companyId: number },
    AppThunkAPIType
>(
    "marketplace/loadCompanyDetails",
    async ({ companyId }, { dispatch, rejectWithValue }) => {
        const url = `api/marketplace/companies/${companyId}`;
        const result = await dispatch(getWithAuth({ url }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as MarketplaceCompanyDetails;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);